import { z } from 'zod';
import { normalizeString } from '../utils/Validations';

export enum FrequencyUnitTime {
    DIAS = "DIAS",
    SEMANAS = "SEMANAS",
    MESES = "MESES",
    ANOS = "ANOS",
    PERIODO_PAGO = "PERIODO_PAGO"
}

export enum PatternStartDateEnum {
    FECHA_REFERENCIA = "FECHA_REFERENCIA",
    CERCANO_FECHA_REFERENCIA = "CERCANO_FECHA_REFERENCIA",
    ANTES_FECHA_REFERENCIA = "ANTES_FECHA_REFERENCIA",
    DESPUES_FECHA_REFERENCIA = "DESPUES_FECHA_REFERENCIA"
}

export enum CompensationTypeEnum {
    ANTES = "ANTES",
    DESPUES = "DESPUES",
    NONE = "none",
    DISABLED = "disabled"
}

export interface FormValues {
    name: string;
    reference_date_id: number;
    frequency_amount: number;
    frequency_unit_time: FrequencyUnitTime;
    pattern_start_date: PatternStartDateEnum;
    day_week_to_start: number | null;
    day_month_to_start: number | null;
    month_to_start: number | null;
    day_period_to_start: number | null;
    compensation_type: CompensationTypeEnum | null;
    compensation_number: number | null;
    compensation_pattern_date_id: number | null;
    full_time_equivalent: string;
}

interface Props {
    t: (key: string, options?: any) => string;
    rows: any[];
    initialValues?: Partial<FormValues>;
}

const FREQUENCY_UNIT_TIME = [
    FrequencyUnitTime.DIAS,
    FrequencyUnitTime.SEMANAS,
    FrequencyUnitTime.MESES,
    FrequencyUnitTime.ANOS,
    FrequencyUnitTime.PERIODO_PAGO
] as const;

const PATTERN_START_DATE = [
    PatternStartDateEnum.FECHA_REFERENCIA,
    PatternStartDateEnum.CERCANO_FECHA_REFERENCIA,
    PatternStartDateEnum.ANTES_FECHA_REFERENCIA,
    PatternStartDateEnum.DESPUES_FECHA_REFERENCIA
] as const;

const COMPENSATION_TYPE = [
    CompensationTypeEnum.ANTES,
    CompensationTypeEnum.DESPUES,
    CompensationTypeEnum.NONE,
    CompensationTypeEnum.DISABLED
] as const;

const createDatePatternSchema = (params: Props) => {
    const { t, rows, initialValues } = params;

    return z.object({
        name: z.string()
            .min(1, t('general.zod.string.required'))
            .transform(val => val.trim())
            .pipe(
                z.string()
                    .min(2, t('general.zod.string.min_chars', { min: 2 }))
                    .max(100, t('general.zod.string.max_chars', { max: 100 }))
                    .refine(val => /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ\-_]+$/.test(val), {
                        message: t('general.zod.string.invalid_characters')
                    })
            ),

        reference_date_id: z.number()
            .min(0, t('general.zod.number.min', { min: 0 })),

        frequency_amount: z.number()
            .min(1, t('general.zod.number.min', { min: 1 }))
            .max(100, t('general.zod.number.max', { max: 100 })),

        frequency_unit_time: z.enum(FREQUENCY_UNIT_TIME),

        pattern_start_date: z.enum(PATTERN_START_DATE),

        day_week_to_start: z.number()
            .min(1, t('general.zod.number.min', { min: 1 }))
            .max(7, t('general.zod.number.max', { max: 7 }))
            .nullable(),

        day_month_to_start: z.number()
            .min(1, t('general.zod.number.min', { min: 1 }))
            .max(31, t('general.zod.number.max', { max: 31 }))
            .nullable(),

        month_to_start: z.number()
            .min(1, t('general.zod.number.min', { min: 1 }))
            .max(12, t('general.zod.number.max', { max: 12 }))
            .nullable(),

        day_period_to_start: z.number()
            .min(1, t('general.zod.number.min', { min: 1 }))
            .max(31, t('general.zod.number.max', { max: 31 }))
            .nullable(),

        compensation_type: z.enum(COMPENSATION_TYPE).nullable(),

        compensation_number: z.number()
            .min(0, t('general.zod.number.min', { min: 0 }))
            .max(100, t('general.zod.number.max', { max: 100 }))
            .nullable(),

        compensation_pattern_date_id: z.number().nullable(),

        full_time_equivalent: z.string()
            .min(1, t('general.zod.string.required'))
    }).superRefine((val, ctx) => {
        // Validación de nombre duplicado
        const normalizedInput = normalizeString(val.name);
        const currentId = initialValues?.name;

        const isDuplicate = rows.some((row: any) =>
            normalizeString(row.name) === normalizedInput &&
            currentId !== row.name
        );

        if (isDuplicate) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('accruals.date_patterns.dialog.alerts.name_exist'),
                path: ['name']
            });
        }

        // Validación de reference_date_id
        if (val.reference_date_id === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('general.zod.string.required'),
                path: ['reference_date_id']
            });
        }

        // Validaciones condicionales según pattern_start_date
        if (val.pattern_start_date !== PatternStartDateEnum.FECHA_REFERENCIA) {

            // Validar day_week_to_start cuando frecuencia es SEMANAS
            if (val.frequency_unit_time === FrequencyUnitTime.SEMANAS && val.day_week_to_start === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.string.required'),
                    path: ['day_week_to_start']
                });
            }

            // Validar day_month_to_start cuando frecuencia es MESES
            if (val.frequency_unit_time === FrequencyUnitTime.MESES && val.day_month_to_start === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.string.required'),
                    path: ['day_month_to_start']
                });
            }

            // Validar month_to_start cuando frecuencia es ANOS
            if (val.frequency_unit_time === FrequencyUnitTime.ANOS && val.month_to_start === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.string.required'),
                    path: ['month_to_start']
                });
            }

            // Validar day_period_to_start cuando frecuencia es PERIODO_PAGO
            if (val.frequency_unit_time === FrequencyUnitTime.PERIODO_PAGO && val.day_period_to_start === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t('general.zod.string.required'),
                    path: ['day_period_to_start']
                });
            }
        }

        // Validación de compensation_number cuando compensation_type no es NONE o DISABLED
        if (
            val.compensation_type !== CompensationTypeEnum.NONE &&
            val.compensation_type !== CompensationTypeEnum.DISABLED &&
            val.compensation_type !== null &&
            val.compensation_number === null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('general.zod.string.required'),
                path: ['compensation_number']
            });
        }

        // Validación del formato de full_time_equivalent (HH:MM o HHH:MM)
        const regex4Digits = /^([0-9]{2}):([0-5][0-9])$/;
        const regex5Digits = /^([0-9]{3}):([0-5][0-9])$/;

        if (!regex4Digits.test(val.full_time_equivalent) && !regex5Digits.test(val.full_time_equivalent)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t('accruals.date_patterns.dialog.alerts.invalid_time_format'),
                path: ['full_time_equivalent']
            });
        } else {
            // Validar que los minutos no excedan 59
            const parts = val.full_time_equivalent.split(':');
            if (parts.length === 2) {
                const minutes = parseInt(parts[1], 10);
                if (minutes > 59) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: t('accruals.date_patterns.dialog.alerts.invalid_minutes'),
                        path: ['full_time_equivalent']
                    });
                }
            }
        }
    });
};

export { createDatePatternSchema };
